/**
 * Groupes professionnels et base d'exposition (docs/SCORING.md §1).
 *
 * Les onze bases sont **dérivées** de l'indice d'exposition professionnelle à
 * l'IA générative de l'Organisation internationale du Travail (Gmyrek et al.,
 * ILO Working Paper 140, 2025), publié sous licence CC BY 4.0 :
 *
 *   base(groupe) = arrondi( 100 × (moyenne(groupe) − 0,09) / (0,70 − 0,09) )
 *
 * où 0,09 et 0,70 sont les scores extrêmes des 427 professions publiées, et
 * moyenne(groupe) la moyenne des scores d'exposition 2025 des professions
 * CITP-08 affectées au groupe — **pondérée par l'emploi depuis le barème
 * 1.3.0** (31/08/2026), sauf là où ce qui manque ne ressemble pas à ce qui
 * reste. Effectifs : ILOSTAT, Côte d'Ivoire 2019, familles CITP à deux
 * chiffres. La règle, son seuil et la limite ivoirienne sont dans
 * `docs/REFERENTIEL.md` §2.
 *
 * Ne pas modifier ces valeurs à la main : elles sont reproduites par
 * `node scripts/deriver-bases.mjs` à partir de `data/oit/`, et `bases.test.ts`
 * échoue si le code et la dérivation divergent. Le tableau de passage et la
 * procédure sont dans `docs/REFERENTIEL.md` §2.
 *
 * Adaptation d'une œuvre originale de l'OIT ; elle n'est pas approuvée par
 * l'OIT (voir `data/oit/SOURCE.md`).
 */
export const GROUPS = {
    COMPTA: { base: 77, label: "Comptabilité et finance" },
    IT: { base: 75, label: "Informatique et données" },
    ADMIN: { base: 66, label: "Administratif et secrétariat" },
    // Seul groupe non pondéré : ce qui lui manque est plus exposé de 0,183 que
    // ce qu'il garde, très au-delà du seuil. Voir `deriver-bases.mjs`.
    RH: { base: 61, label: "Ressources humaines" },
    JURIDIQUE: { base: 55, label: "Juridique" },
    CREA: { base: 47, label: "Création, contenu et communication" },
    COMMERCIAL: { base: 45, label: "Commercial et relation client" },
    DIRECTION: { base: 44, label: "Direction et encadrement" },
    ENSEIGNEMENT: { base: 38, label: "Enseignement et formation" },
    SANTE: { base: 24, label: "Santé et social" },
    TERRAIN: { base: 15, label: "Métiers techniques et de terrain" },
};
/** Groupe appliqué quand le modèle renvoie une valeur hors liste fermée. */
export const DEFAULT_GROUP = "ADMIN";
export const GROUP_CODES = Object.keys(GROUPS);
/**
 * L'écart des bases — calculé, jamais recopié. 02/09/2026.
 *
 * L'accueil publie cet écart comme l'un de ses trois seuls chiffres, en
 * affirmant qu'il est « vérifié par `bases.test.ts` ». Il ne l'était pas : la
 * valeur était saisie à la main dans le composant, et elle est restée sur
 * « 21 à 77 » — l'écart du barème 1.1.0, non pondéré — pendant que le barème
 * 1.3.0 déplaçait la borne basse à 15.
 *
 * La recette gardait bien les onze bases contre la source de l'OIT ; ce
 * qu'elle ne pouvait pas voir, c'est que la page qui les cite ne les lisait
 * pas. Publier un chiffre faux **sur la page qui demande qu'on lui fasse
 * confiance**, en s'appuyant sur une preuve qui porte sur autre chose, est le
 * faux signal de la règle n° 6 dans sa forme la plus coûteuse.
 *
 * Deux constantes dérivées ferment la question : il n'y a plus de valeur à
 * tenir à jour, donc plus de valeur à oublier.
 */
export const BASE_MIN = Math.min(...Object.values(GROUPS).map((g) => g.base));
export const BASE_MAX = Math.max(...Object.values(GROUPS).map((g) => g.base));
export function isGroupCode(value) {
    return typeof value === "string" && value in GROUPS;
}
/**
 * Normalise le groupe renvoyé par le modèle. Toute valeur inconnue est traitée
 * comme ADMIN, et le drapeau `fallback` invite l'appelant à émettre
 * l'évènement `scoring_group_fallback`.
 */
export function resolveGroup(value) {
    if (isGroupCode(value))
        return { code: value, fallback: false };
    return { code: DEFAULT_GROUP, fallback: true };
}
